import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { LibraryManager, ResourceType } from 'libs.library_manager';
import { Input, SegmentedControl, Select, Toggle, Tooltip, Icon, Text, useLocalizeMessage, } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { BarDataSection } from './bar_data_section.component';
import { FieldPicker } from './field_picker.component';
import { MultivariateDataSection } from './multivariate_data_section.component';
import { NumberDataSection } from './number_data_section.component';
import { PieDataSection } from './pie_data_section.component';
import { TableDataSection } from './table_data_section.component';
import { StudiesContext } from '../../../../contexts';
import { toSnakeCase } from '../../../../data_views/utils/utils';
import { isBuilderBarChartType } from '../../../utils';
export const DataSection = ({ state, onChange, isEditable, cubeConfigId, onAvailableDimensionsChange, onAvailableMeasuresChange, }) => {
    const translate = useLocalizeMessage();
    const { entityId } = useContext(AuthContext);
    const { studies, isLoading: isLoadingStudies } = useContext(StudiesContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    // ── Study options ──────────────────────────────────────────────────────
    const studyOptions = useMemo(() => studies.map((study) => ({
        label: study.name,
        value: study.id.toString(),
    })), [studies]);
    const selectedStudy = useMemo(() => studyOptions.find((s) => s.value === state.studyId), [studyOptions, state.studyId]);
    // ── Environment fetching ───────────────────────────────────────────────
    const fetchEnvironments = useCallback(async () => {
        if (!state.studyId) {
            return [];
        }
        try {
            const { data: response } = await momClient.get(`/v1/entities/${entityId}/studies/${state.studyId}/environments`, {
                params: {
                    clusterTypes: 'dev',
                    withCortexAccess: true,
                },
            });
            return response.data.filter((env) => !env.deployment.isProd);
        }
        catch {
            return [];
        }
    }, [state.studyId, entityId, momClient]);
    const { data: environments = [], isLoading: isLoadingEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', state.studyId, 'environments'],
        queryFn: fetchEnvironments,
        enabled: !!entityId && !!state.studyId,
        refetchOnWindowFocus: false,
    });
    const environmentOptions = useMemo(() => environments.map((env) => ({
        label: env.name,
        value: env.cortexId || env.id.toString(),
    })), [environments]);
    const selectedEnvironment = useMemo(() => environmentOptions.find((e) => e.value === state.testEnvironmentId), [environmentOptions, state.testEnvironmentId]);
    // ── Dataset schema — fetch dimensions/measures directly from the dataset resource ──
    // Same approach as the dataset detail page: fetchDataViewById → getResourceById
    const fetchDatasetSchema = useCallback(async () => {
        if (!cubeConfigId) {
            return {
                cubeName: '',
                dimensions: {},
                measures: {},
            };
        }
        const libraryManager = new LibraryManager({ axiosInstance: libraryClient });
        const resource = await libraryManager.getResourceById(cubeConfigId, ResourceType.CUBE_CONFIG);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = resource?.data || {};
        return {
            cubeName: data.name ? toSnakeCase(data.name) : '',
            dimensions: (data.dimensions || {}),
            measures: (data.measures || {}),
        };
    }, [cubeConfigId, libraryClient]);
    const { data: datasetSchema, isLoading: isLoadingSchema } = useQuery({
        queryKey: ['dataset-schema', cubeConfigId],
        queryFn: fetchDatasetSchema,
        enabled: !!cubeConfigId,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
    });
    const cubeName = datasetSchema?.cubeName || '';
    // Map dataset dimensions/measures to ColumnConfig for TableDataSection
    const availableDimensions = useMemo(() => Object.entries(datasetSchema?.dimensions || {})
        .filter(([, def]) => def.public !== false)
        .map(([key, def]) => ({
        key: cubeName ? `${cubeName}.${key}` : key,
        name: def.title || key,
        type: def.type,
        role: 'dimension',
    })), [datasetSchema?.dimensions, cubeName]);
    const availableMeasures = useMemo(() => Object.entries(datasetSchema?.measures || {})
        .filter(([, def]) => def.public !== false)
        .map(([key, def]) => ({
        key: cubeName ? `${cubeName}.${key}` : key,
        name: def.title || key,
        type: def.type,
        role: 'measure',
    })), [datasetSchema?.measures, cubeName]);
    useEffect(() => {
        onAvailableDimensionsChange?.(availableDimensions);
    }, [availableDimensions, onAvailableDimensionsChange]);
    useEffect(() => {
        onAvailableMeasuresChange?.(availableMeasures);
    }, [availableMeasures, onAvailableMeasuresChange]);
    // ── Handlers ───────────────────────────────────────────────────────────
    const handleStudyChange = useCallback((option) => {
        const isInitialSelection = !state.studyId;
        onChange({
            ...state,
            studyId: option.value,
            testEnvironmentId: isInitialSelection ? state.testEnvironmentId : undefined,
        });
    }, [state, onChange]);
    const handleEnvironmentChange = useCallback((option) => {
        onChange({
            ...state,
            testEnvironmentId: option.value,
        });
    }, [state, onChange]);
    const handleColumnsChange = useCallback((columns) => {
        onChange({ ...state, columns });
    }, [state, onChange]);
    const handleXAxisChange = useCallback((xAxis) => {
        onChange({ ...state, xAxis });
    }, [state, onChange]);
    const handleYAxisChange = useCallback((yAxis) => {
        onChange({ ...state, yAxis });
    }, [state, onChange]);
    const handleSplitByChange = useCallback((splitBy) => {
        onChange({ ...state, splitBy });
    }, [state, onChange]);
    const handleNumberValuesChange = useCallback((numberValues) => {
        onChange({ ...state, numberValues });
    }, [state, onChange]);
    const handleAdditionalMeasuresChange = useCallback((additionalMeasures) => {
        onChange({ ...state, additionalMeasures });
    }, [state, onChange]);
    const handleDotSizeMappingChange = useCallback((item) => {
        const mapping = item.id;
        onChange({
            ...state,
            dotSizeMapping: mapping,
            // Clear the other field when switching modes
            dotSizeField: mapping === 'constant' ? undefined : state.dotSizeField,
            dotSizeConstant: mapping === 'variable' ? undefined : state.dotSizeConstant,
        });
    }, [state, onChange]);
    const handleDotSizeFieldChange = useCallback((field) => {
        const matchingField = availableMeasures.find((option) => option.key === field.key);
        onChange({
            ...state,
            dotSizeField: matchingField ? { ...matchingField } : { key: field.key, name: field.name, type: field.type },
        });
    }, [state, onChange, availableMeasures]);
    const handleDotSizeConstantChange = useCallback((e) => {
        const { value } = e.target;
        onChange({ ...state, dotSizeConstant: value === '' ? undefined : Number(value) });
    }, [state, onChange]);
    const handleNormalizeBubbleSizeToggle = useCallback((checked) => {
        onChange({
            ...state,
            normalizeBubbleSize: checked,
            ...(checked ? {} : { bubbleSizeMinOverride: undefined, bubbleSizeMaxOverride: undefined }),
        });
    }, [state, onChange]);
    const handleBubbleSizeMinOverrideChange = useCallback((e) => {
        const { value } = e.target;
        onChange({ ...state, bubbleSizeMinOverride: value === '' ? undefined : Number(value) });
    }, [state, onChange]);
    const handleBubbleSizeMaxOverrideChange = useCallback((e) => {
        const { value } = e.target;
        onChange({ ...state, bubbleSizeMaxOverride: value === '' ? undefined : Number(value) });
    }, [state, onChange]);
    const dotSizeMappingItems = useMemo(() => [
        { id: 'variable', label: translate('Map to variable') },
        { id: 'constant', label: translate('Map to constant') },
    ], [translate]);
    // The bubble (variable dot size) field cannot collide with the X or Y axes.
    const dotSizeOptions = useMemo(() => availableMeasures.filter((option) => option.key !== state.xAxis?.key && option.key !== state.yAxis?.key), [availableMeasures, state.xAxis?.key, state.yAxis?.key]);
    const isTable = state.chartType === 'table';
    const isNumberChart = state.chartType === 'number';
    const isPie = state.chartType === 'pie';
    const isMultivariate = state.chartType === 'multivariate';
    const isScatter = state.chartType === 'scatter';
    const isLine = state.chartType === 'line';
    const isMultiLine = state.chartType === 'multiLine';
    const isBarType = isBuilderBarChartType(state.chartType);
    const isAxisBasedType = isBarType || isScatter || isLine || isMultiLine;
    const showSplitBy = state.chartType === 'groupedBar' || state.chartType === 'stackedBar' || isScatter || isMultiLine;
    const splitByRequired = state.chartType === 'groupedBar' || state.chartType === 'stackedBar' || isMultiLine;
    const hasSchemaOrSavedColumns = (state.columns && state.columns.length > 0) || !!cubeConfigId;
    const hasSchemaOrSavedBarFields = !!cubeConfigId || !!state.xAxis || !!state.yAxis || !!state.splitBy;
    const hasSchemaOrSavedNumberValues = !!cubeConfigId || !!state.numberValues?.length;
    return (_jsxs("div", { className: 'flex flex-col gap-4', children: [_jsx("div", { id: 'chart-study-select', children: _jsx(Select, { label: translate('Study'), required: true, placeholder: translate('Select a study'), options: studyOptions, value: selectedStudy, onChange: handleStudyChange, width: 'full', disabled: isLoadingStudies, searchableOptions: true }) }), _jsx("div", { id: 'chart-env-select', children: _jsx(Select, { label: translate('Test environment'), required: true, placeholder: translate('Select an environment'), options: environmentOptions, value: selectedEnvironment, onChange: handleEnvironmentChange, width: 'full', disabled: !state.studyId || isLoadingEnvironments, searchableOptions: true }) }), isTable && hasSchemaOrSavedColumns && (_jsxs(_Fragment, { children: [_jsx("hr", { className: 'border-neutral-200' }), _jsx(TableDataSection, { columns: state.columns || [], onColumnsChange: handleColumnsChange, isEditable: isEditable, availableDimensions: availableDimensions, availableMeasures: availableMeasures, isLoadingSchema: isLoadingSchema })] })), isAxisBasedType && hasSchemaOrSavedBarFields && (_jsxs(_Fragment, { children: [_jsx("hr", { className: 'border-neutral-200' }), _jsx(BarDataSection, { xAxis: state.xAxis, yAxis: state.yAxis, splitBy: state.splitBy, onXAxisChange: handleXAxisChange, onYAxisChange: handleYAxisChange, onSplitByChange: handleSplitByChange, availableDimensions: availableDimensions, availableMeasures: availableMeasures, xAxisOptions: isScatter ? availableMeasures : undefined, exclusiveFieldKey: isScatter && state.dotSizeMapping === 'variable' ? state.dotSizeField?.key : undefined, showSplitBy: showSplitBy, splitByRequired: splitByRequired, isEditable: isEditable }), isScatter && (_jsxs("div", { className: 'flex flex-col gap-4 mt-4', children: [_jsxs("div", { children: [_jsx("label", { className: 'font-medium text-sm text-neutral-900 mb-2 block', children: translate('Dot area size mapping') }), _jsx("div", { className: '[&>div]:w-full [&_[role=tablist]]:w-full [&_button]:flex-1', children: _jsx(SegmentedControl, { items: dotSizeMappingItems, activeItemIndex: state.dotSizeMapping === 'variable' ? 0 : 1, onItemClick: handleDotSizeMappingChange }) })] }), state.dotSizeMapping === 'variable' ? (_jsxs(_Fragment, { children: [_jsx(FieldPicker, { label: translate('Dot area size'), value: state.dotSizeField, options: dotSizeOptions, onChange: handleDotSizeFieldChange, disabled: !isEditable }), state.dotSizeField && (_jsxs("div", { className: 'flex flex-col gap-3 mt-2', children: [_jsxs("div", { className: 'flex items-center gap-2', children: [_jsx(Toggle, { label: translate('Normalize bubble size'), checked: !!state.normalizeBubbleSize, onChange: handleNormalizeBubbleSizeToggle, disabled: !isEditable }), _jsx(Tooltip, { title: translate('Min and max are automatically calculated from the size measure data. You can manually override either bound.'), placement: 'end', delayShow: 300, children: _jsx(Icon, { name: 'info', size: 'xs', color: 'gray' }) })] }), state.normalizeBubbleSize && (_jsxs("div", { className: 'flex flex-col gap-3 pl-1', children: [_jsxs("div", { children: [_jsx(Text, { size: 'xs', className: 'text-neutral-500 mb-1', children: translate('Minimum bound') }), _jsx(Input, { id: 'bubble-size-min-override', label: translate('Min override'), type: 'number', value: state.bubbleSizeMinOverride?.toString() ?? '', onChange: handleBubbleSizeMinOverrideChange, placeholder: translate('Auto-calculated'), width: 'full', disabled: !isEditable })] }), _jsxs("div", { children: [_jsx(Text, { size: 'xs', className: 'text-neutral-500 mb-1', children: translate('Maximum bound') }), _jsx(Input, { id: 'bubble-size-max-override', label: translate('Max override'), type: 'number', value: state.bubbleSizeMaxOverride?.toString() ?? '', onChange: handleBubbleSizeMaxOverrideChange, placeholder: translate('Auto-calculated'), width: 'full', disabled: !isEditable })] })] }))] }))] })) : (_jsx(Input, { id: 'dot-size-constant', label: translate('Dot area size'), type: 'number', value: state.dotSizeConstant?.toString() || '', onChange: handleDotSizeConstantChange, width: 'full', disabled: !isEditable }))] }))] })), isPie && hasSchemaOrSavedBarFields && (_jsxs(_Fragment, { children: [_jsx("hr", { className: 'border-neutral-200' }), _jsx(PieDataSection, { xAxis: state.xAxis, yAxis: state.yAxis, onXAxisChange: handleXAxisChange, onYAxisChange: handleYAxisChange, availableDimensions: availableDimensions, availableMeasures: availableMeasures, isEditable: isEditable })] })), isMultivariate && hasSchemaOrSavedBarFields && (_jsxs(_Fragment, { children: [_jsx("hr", { className: 'border-neutral-200' }), _jsx(MultivariateDataSection, { xAxis: state.xAxis, yAxis: state.yAxis, additionalMeasures: state.additionalMeasures || [], onXAxisChange: handleXAxisChange, onYAxisChange: handleYAxisChange, onAdditionalMeasuresChange: handleAdditionalMeasuresChange, availableDimensions: availableDimensions, availableMeasures: availableMeasures, isEditable: isEditable })] })), isNumberChart && hasSchemaOrSavedNumberValues && (_jsxs(_Fragment, { children: [_jsx("hr", { className: 'border-neutral-200' }), _jsx(NumberDataSection, { numberValues: state.numberValues || [], onNumberValuesChange: handleNumberValuesChange, availableMeasures: availableMeasures, isEditable: isEditable })] }))] }));
};
